import React, { FC, useState, useEffect, useCallback } from 'react'
import * as app from '../styles/App.styled'
import * as work from '../styles/Work.styled'

// https://commerce.nearform.com/open-source/nuka-carousel/docs/api/

// https://www.npmjs.com/package/nuka-carousel

// interface CarouselProps {
//   slides: React.ReactNode[]
// }

interface CarouselProps {
  slides: React.ReactNode[]
  autoplay?: boolean
  interval?: number // Interval for autoplay in milliseconds
}

const Carousel:FC<CarouselProps> = ({
  slides,
  autoplay = false,
  interval = 3000,
}) => {
  const [$currentIndex, setCurrentIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)

  const nextSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % slides.length)
  }, [slides.length])

  const prevSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? slides.length - 1 : prevIndex - 1
    )
  }

  // Autoplay Effect
  useEffect(() => {
    if (autoplay && !isPaused) {
      const timer = setInterval(nextSlide, interval)
      return () => clearInterval(timer)
    }
  }, [
    autoplay,
    isPaused,
    interval,
    slides.length,
    nextSlide,
  ])

  return (
    <work.StyledCarouselContainer
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <work.StyledSlidesContainer
        $currentIndex={$currentIndex}
        $totalSlides={slides.length}
      >
        {slides.map((slide, index) => (
          <work.StyledSlide key={index}>
            {slide}
          </work.StyledSlide>
        ))}
      </work.StyledSlidesContainer>
      <work.StyledButton
        $absolutePosition='left'
        onClick={prevSlide}
      >
        <>&#9664;</>
      </work.StyledButton>
      <work.StyledButton
        $absolutePosition='right'
        onClick={nextSlide}
      >
        <>&#9654;</>
      </work.StyledButton>
      <work.StyledIndicators>
        {slides.map((_, index) => (
          <work.StyledIndicator
            key={index}
            $isActive={index === $currentIndex}
            onClick={() => setCurrentIndex(index)}
          />
        ))}
      </work.StyledIndicators>
    </work.StyledCarouselContainer>
  )
}

export const Work: FC = () => {
  const slides = [
    <div key={0}>
      <strong>SOFTWARE ENGINEER | PRATT & WHITNEY</strong>
      <p>2018 - Present</p>
      <ul>
        <li>Design, develop, and deploy a web application to track workflow for engine component repair procedure qualification.</li>
        <li>Design, develop, and deploy a web application to visualize repair procedure and tooling dependencies to aid in priority planning.</li>
        <li>Deploy and manage relational and NoSQL databases for apps and services.</li>
        <li>Develop and deploy API services for use across the company.</li>
        <li>Code master for several projects: review and approve pull requests.</li>
        <li>Gather requirements from users for new and existing applications.</li>
      </ul>
    </div>,
    <div key={1}>
      <strong>SOFTWARE DEVELOPER | PRATT & WHITNEY</strong>
      <p>2012 - 2018</p>
      <ul>
        <li>Developed automated tools to analyze and assess risk associated with jet engine sales and maintenance plans.</li>
        <li>Developed SQL views, macros, stored procedures for use in analysis tool suite.</li>
        <li>Standardized modules for financial analysis and risk assessment.</li>
        <li>Coordinated with QA team to create test plans prior to release.</li>
        <li>Gathered requirements from users for new and existing tools.</li>
      </ul>
    </div>,
    <div key={2}>
      <strong>TECHNICAL ANALYST | PRATT & WHITNEY</strong>
      <p>2011 - 2012</p>
      <ul>
        <li>Supported jet engine sales campaigns and existing contract restructures; worked on team that closed a deal with over $250 million (NPV) in sales.</li>
        <li>Supported quarterly tasks to re-baseline existing maintenance contract financial metrics.</li>
        <li>Generated customer specific fleet maintenance forecasts and analyzed associated risk.</li>
        <li>Created language for new engine sales contracts and contract restructures.</li>
      </ul>
    </div>,
    <div key={3}>
      <strong>PROJECT ENGINEER | PRATT & WHITNEY</strong>
      <p>2008 - 2011</p>
      <ul>
        <li>Managed jet engine part design tasks to improve performance and manufacturability; reported weekly task status to Program office.</li>
        <li>Developed business cases for jet engine part cost reduction projects: created cost/benefit analysis, developed schedule, estimated budget.</li>
        <li>Managed selection process of cost reduction projects based on NPV analysis and risk weighting.</li>
        <li>Managed all turbine test activity; coordinated activities of multiple part teams; disseminated engine part teams’ test requirements to Test Engineers.</li>
        <li>Coordinated team trips to USAF bases for engine part inspection to ascertain part performance; presented results to USAF representative personnel.</li>
      </ul>
    </div>,
    <div key={4}>
      <strong>TURBINE DURABILITY ENGINEER | PRATT & WHITNEY</strong>
      <p>2002 - 2008</p>
      <ul>
        <li>Managed jet engine part design tasks to improve performance and manufacturability; reported weekly task status to Program office.</li>
        <li>Designed gas turbine airfoil internal cooling passages and external surface film cooling to optimize metal temperatures for F135 and other engines.</li>
        <li>Calculated service life of gas turbine airfoils based on failure modes and mission parameters.</li>
        <li>Inspected engine run hardware to gauge effectiveness of cooling scheme design and expand computational models’ empirical database.</li>
        <li>Developed Engineering Standard Work for cooling scheme design workflow.</li>
      </ul>
    </div>,
  ]

  return (
    <app.StyledSection>
      <Carousel slides={slides} />
    </app.StyledSection>
  )
}
